import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWS } from '../contexts/WebSocketContext';
import { publishingAPI } from '../services/api';
import { FiRefreshCw, FiClock, FiCheck, FiAlertCircle, FiX, FiMonitor, FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const PublishingTasks = () => {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [publishingTasks, setPublishingTasks] = useState([]);
  const [publishingStatus, setPublishingStatus] = useState([]);
  const [fileStatus, setFileStatus] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalTasks, setTotalTasks] = useState(0);

  const fetchPublishingData = async (showRefreshSpinner = false) => {
    if (showRefreshSpinner) setRefreshing(true);
    
    try {
      console.log('=== Frontend Publishing Data Fetch ===');
      console.log('User object:', user);
      console.log('User compID:', user?.compID);
      console.log('Pagination - Page:', currentPage, 'PerPage:', itemsPerPage);
      console.log('=====================================');
      
      const [tasksResponse, status, fileStatusData] = await Promise.all([
        publishingAPI.getPublishingTasks(user.compID, currentPage, itemsPerPage),
        publishingAPI.getPublishingStatus(user.compID),
        publishingAPI.getFileStatus(user.compID)
      ]);
      
      console.log('Tasks response:', tasksResponse);
      
      setPublishingTasks(tasksResponse?.tasks || []);
      setTotalTasks(tasksResponse?.total || 0);
      setPublishingStatus(status || []);
      setFileStatus(fileStatusData || []);
    } catch (error) {
      console.error('Error fetching publishing data:', error);
    } finally {
      setLoading(false);
      if (showRefreshSpinner) setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.compID) {
      fetchPublishingData();
    }
  }, [user?.compID, currentPage, itemsPerPage]);

  // Subscribe to real-time publishing updates via WebSocket
  useEffect(() => {
    const unsub1 = subscribe('publishing:progress', () => {
      // Refresh data when progress updates come in (debounced by React batching)
      fetchPublishingData();
    });
    const unsub2 = subscribe('publishing:complete', () => {
      fetchPublishingData();
    });
    const unsub3 = subscribe('publishing:created', () => {
      fetchPublishingData();
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [subscribe]);

  // Pagination helper functions
  const totalPages = Math.ceil(totalTasks / itemsPerPage);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedTask(null); // Clear selected task when changing pages
  };
  
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    setSelectedTask(null);
  };
  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheck className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <FiX className="h-5 w-5 text-red-500" />;
      case 'in_progress':
        return <FiRefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
      default:
        return <FiClock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTaskProgress = (taskID) => {
    const taskStatus = publishingStatus.filter(s => s.taskID === taskID);
    const completedAgents = taskStatus.filter(s => s.status === 'completed').length;
    const failedAgents = taskStatus.filter(s => s.status === 'failed').length;
    const inProgressAgents = taskStatus.filter(s => s.status === 'in_progress').length;
    const total = taskStatus.length;

    return {
      completed: completedAgents,
      failed: failedAgents,
      inProgress: inProgressAgents,
      total
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading publishing tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Publishing Tasks</h1>
          <p className="mt-2 text-gray-600">
            Monitor content publishing tasks and their status across your devices
          </p>
        </div>
        
        <button
          onClick={() => fetchPublishingData(true)}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
        {/* Tasks List */}
        <div className="lg:col-span-2 bg-white shadow rounded-lg flex flex-col">
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Publishing Tasks</h2>
              <div className="flex items-center space-x-4">
                {/* Items per page selector */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                {/* Total count */}
                <span className="text-sm text-gray-500">
                  {totalTasks} total tasks
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-gray-200">
            {publishingTasks.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <FiMonitor className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>No publishing tasks found</p>
                <p className="text-sm">Tasks will appear here after you publish content</p>
              </div>
            ) : (
              publishingTasks.map(task => {
                const progress = getTaskProgress(task.taskID);
                // Backend already parses agentGroupIDs, so it should be an array
                const agentGroupIDs = Array.isArray(task.agentGroupIDs) ? task.agentGroupIDs : [];
                
                return (
                  <div
                    key={task.taskID}
                    className={`p-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedTask?.taskID === task.taskID ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(task.status)}
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Task #{task.taskID}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {task.mediaGroupName || `Media Group ${task.mediaGroupID}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(task.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {progress.total > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Progress</span>
                          <span>{progress.completed}/{progress.total} devices</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{agentGroupIDs.length} agent group(s)</span>
                      {task.scheduledTime && (
                        <span className="flex items-center space-x-1">
                          <FiCalendar className="h-3 w-3" />
                          <span>Scheduled: {formatDate(task.scheduledTime)}</span>
                        </span>
                      )}
                    </div>

                    {task.message && (
                      <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                        {task.message}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Pagination Controls */}
          {totalTasks > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalTasks)} to{' '}
                  {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks
                </span>
              </div>
              
              <div className="flex items-center space-x-1">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <FiChevronLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {getPageNumbers().map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded text-sm ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                
                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <span>Next</span>
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Task Details */}
        <div className="bg-white shadow rounded-lg flex flex-col">
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">Task Details</h2>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            {selectedTask ? (
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Task Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Task ID:</span>
                      <span className="font-medium">#{selectedTask.taskID}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedTask.status)}`}>
                        {selectedTask.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created:</span>
                      <span>{formatDate(selectedTask.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Media Group:</span>
                      <span>{selectedTask.mediaGroupName}</span>
                    </div>
                  </div>
                </div>

                {/* Agent Status */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Device Status</h3>
                  <div className="space-y-2">
                    {publishingStatus
                      .filter(status => status.taskID === selectedTask.taskID)
                      .map(status => (
                        <div key={`${status.taskID}-${status.agentID}`} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{status.agentName || `Agent ${status.agentID}`}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(status.status)}`}>
                            {status.status}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* File Status */}
                {fileStatus.filter(fs => fs.taskID === selectedTask.taskID).length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">File Progress</h3>
                    <div className="space-y-2">
                      {fileStatus
                        .filter(fs => fs.taskID === selectedTask.taskID)
                        .map(fs => (
                          <div key={`${fs.taskID}-${fs.agentID}-${fs.mediaID}`} className="text-sm">
                            <div className="flex justify-between mb-1">
                              <span className="text-gray-600">
                                {fs.agentName || `Agent ${fs.agentID}`} - File {fs.mediaID}
                              </span>
                              <span>{fs.downloadProgress || 0}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div
                                className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                style={{ width: `${fs.downloadProgress || 0}%` }}
                              ></div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {selectedTask.message && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Message</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                      {selectedTask.message}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <FiMonitor className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Select a task to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishingTasks;
